import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import partCodesService from "services/part-code-service";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Chip from "@mui/material/Chip";

import DataTable from "e4acomponents/Tables/DataTable";


export default function AwaitingApproval() {

    const [partCodes, setPartCodes] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                const response = await partCodesService.getPartsAwaitingApproval();
                setPartCodes(response.data.attributes);
            } catch (error) {
                console.error("Error fetching recent changes:", error);
            }
        })();
    }, []);

    useEffect(() => {
        setTableData(getRows(partCodes));
    }, [partCodes]);

    const fullPartCode = (partCode) => {
        return `E4A-${partCode.partCategory.categoryCode}-${fillWithZeros(partCode.unique_num)}-${partCode.revision_num}`;
    }

    const formatDate = (date) => {
        const now = new Date();

        const changeDate = new Date(date);
        const diffMs = now - changeDate;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffWeeks = Math.floor(diffDays / 7);
        const diffMonths = Math.floor(diffDays / 30);

        if (diffMs < 60000) return `${Math.floor(diffMs / 1000)} seconds ago`;
        if (diffMins < 60) return `${diffMins} minutes ago`;
        if (diffHours < 24) return `${diffHours} hours ago`;
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffMonths < 1) return `${diffWeeks} weeks ago`;
        return `${diffMonths} months ago`;
    }

    const fillWithZeros = (num) => {
        return num.toString().padStart(5, '0');
    }

    const statusColor = (status) => {
        switch (status) {
            case "Approved":
                return "success";
            case "Pending Approval":
                return "warning";
            case "Rejected":
                return "error";
        }
    }

    const getRows = (info) => {
        const sortedInfo = [...(info || [])].sort((a, b) =>
            new Date(b.updated_at) - new Date(a.updated_at)
        );
        return sortedInfo.map((partCode) => {
            return {
                id: partCode._id,
                partCode: fullPartCode(partCode),
                category: partCode.partCategory.description,
                status: partCode.status,
                statusChip: <Chip label={partCode.status} color={statusColor(partCode.status)} variant="outlined" />,
                date: formatDate(partCode.updated_at),
            };
        });
    }

    const dataTableData = {
        columns: [
            {
                Header: "Part Code",
                accessor: "partCode",
                Cell: (info) => {
                    return (
                        <MDTypography
                            component="a"
                            href="#"
                            variant="button"
                            fontWeight="medium"
                            onClick={() => goToViewPage(info.cell.row.original.id)}
                            sx={{ cursor: "pointer", textDecoration: "none", color: "#344767" }}
                        >
                            {info.cell.value}
                        </MDTypography>
                    );
                }
            },
            { Header: "Part Category", accessor: "category" },
            { 
                Header: "Status", 
                accessor: "status",
                Cell: ({row})=> row.original.statusChip 
            },
            { Header: "Date", accessor: "date" },
        ],
        rows: tableData
    }

    useEffect(() => {
        if (tableData.length > 0) {
            setIsLoading(false);
        }
    }, [tableData]);

    const goToApprovalPage = () => {
        navigate("/part-code-management/approval-page");
    }
    // Handler to navigate to the specific view page of the part code
    const goToViewPage = (id) => {
        navigate(`/part-code-management/view-part-code/${id}`);
    }

    return (
        <Card sx={{ width: "100%", overflow: "visible", height: "100%" }}>
            <MDBox display="flex">
                <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="4rem"
                    height="4rem"
                    variant="gradient"
                    bgColor="warning"
                    color="white"
                    shadow="md"
                    borderRadius="xl"
                    ml={3}
                    mt={-2}
                >
                    <Icon fontSize="medium" color="inherit">
                        grading
                    </Icon>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" width="100%">

                    <MDTypography variant="h5" sx={{ mt: 2, mb: 1, ml: 2 }}>
                        Awaiting Approval
                    </MDTypography>

                    <MDBox display="flex" mr={2} alignItems="center" justifyContent="center">
                        <a onClick={goToApprovalPage} style={{ cursor: 'pointer' }}>
                            <MDTypography variant="button" color="text" fontWeight="regular" sx={{ mt: 2, mb: 1, ml: 2 }}>
                                View All
                            </MDTypography>
                        </a>
                        <a onClick={goToApprovalPage} style={{ cursor: 'pointer' }}>
                            <ArrowForwardIosIcon sx={{ mt: 2, mb: 1, ml: 1, mr: 2 }} fontSize="small" color="inherit" />
                        </a>
                    </MDBox>

                </MDBox>

            </MDBox>
            <MDBox p={2} >
                <DataTable
                    table={dataTableData}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    isLoading={isLoading}
                    noEndBorder
                    tableId="awaiting-approval-table"
                />
            </MDBox>
        </Card>
    )
}